import React, { useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { triggerSelectionHaptic } from '../utils/webHaptics';

interface WebUserMenuDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    userDisplayName: string;
    userPhone?: string;
    username?: string;
    userPhotoUrl?: string | null;
    userInitial: string;
    onLogout: () => void;
    loggingOut: boolean;
}

export const WebUserMenuDropdown: React.FC<WebUserMenuDropdownProps> = ({
    isOpen,
    onClose,
    userDisplayName,
    userPhone,
    username,
    userPhotoUrl,
    userInitial,
    onLogout,
    loggingOut,
}) => {
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const cleanUsername = String(username || '').trim().replace(/^@/, '');

    return (
        <div
            className="absolute right-0 mt-2.5 w-72 rounded-2xl bg-white p-2.5 shadow-2xl border border-slate-100 ring-1 ring-black/5 animate-scaleUp z-50 space-y-2 text-left"
            onClick={(e) => e.stopPropagation()}
        >
            {/* User Profile Info Card */}
            <div className="p-3 bg-slate-50/80 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D7E1EA] text-[#475569] font-bold text-xs flex items-center justify-center border-2 border-white shadow-xs overflow-hidden shrink-0">
                    {userPhotoUrl ? (
                        <img src={userPhotoUrl} alt={userDisplayName} className="w-full h-full object-cover" />
                    ) : (
                        <span>{userInitial}</span>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-extrabold text-slate-900 truncate leading-tight">{userDisplayName}</p>
                    {cleanUsername ? (
                        <p className="text-[11px] font-semibold text-teal-600 truncate mt-0.5">@{cleanUsername}</p>
                    ) : null}
                    {userPhone && userPhone !== userDisplayName ? (
                        <p className="text-[11px] font-medium text-slate-400 truncate mt-0.5">{userPhone}</p>
                    ) : null}
                </div>
            </div>

            {/* Actions */}
            <div className="pt-0.5">
                <button
                    type="button"
                    aria-label="Log out"
                    onClick={() => {
                        triggerSelectionHaptic();
                        onClose();
                        onLogout();
                    }}
                    disabled={loggingOut}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-bold text-red-600 hover:bg-red-50/80 active:scale-[0.98] transition-all disabled:opacity-50 text-left cursor-pointer"
                >
                    <LogOut className="w-3.5 h-3.5 stroke-[2.2]" />
                    <span>{loggingOut ? 'Signing out...' : 'Log out'}</span>
                </button>
            </div>
        </div>
    );
};

export default WebUserMenuDropdown;
